import { lazy, type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { codeCoverageRouteDefinitions } from '@/features/codeCoverage/routes/routeDefinitions';
import { ProjectSettingsPage } from '@/features/projects/routes';
import {
  BuildsIcon,
  DriveFileMoveIcon,
  FeaturesIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ReleasesIcon,
  SettingsIcon,
  StagesIcon,
  SuitesIcon,
  TestsIcon,
} from '@/ui/primitives';
import { AUTH_ROUTE_PATHS } from './authRoutePaths';
import type { AuthError, AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto } from '@flowqa/shared';

const DocsPage = lazy(async () => {
  const module = await import('../../pages/DocsPage');
  return { default: module.DocsPage };
});
const LandingPage = lazy(async () => {
  const module = await import('../../pages/LandingPage');
  return { default: module.LandingPage };
});
const PreviewPage = lazy(async () => {
  const module = await import('../../pages/PreviewPage');
  return { default: module.PreviewPage };
});
const PlanPage = lazy(async () => {
  const module = await import('../../pages/PlanPage');
  return { default: module.PlanPage };
});
const PrivacyRequestsPage = lazy(async () => {
  const module = await import('../../pages/PrivacyRequestsPage');
  return { default: module.PrivacyRequestsPage };
});
const ReportsPage = lazy(async () => {
  const module = await import('../../pages/ReportsPage');
  return { default: module.ReportsPage };
});
const ResetPasswordConfirmPage = lazy(async () => {
  const module = await import('../../pages/ResetPasswordConfirmPage');
  return { default: module.ResetPasswordConfirmPage };
});
const ResetPasswordPage = lazy(async () => {
  const module = await import('../../pages/ResetPasswordPage');
  return { default: module.ResetPasswordPage };
});
const SessionExpired = lazy(async () => {
  const module = await import('../../pages/SessionExpired');
  return { default: module.SessionExpired };
});
const SuperuserPage = lazy(async () => {
  const module = await import('../../pages/SuperuserPage');
  return { default: module.SuperuserPage };
});
const TermlyPolicyEmbed = lazy(async () => {
  const module = await import('../../pages/TermlyPolicyEmbed');
  return { default: module.TermlyPolicyEmbed };
});
const UserSettingsPage = lazy(async () => {
  const module = await import('../../pages/UserSettingsPage');
  return { default: module.UserSettingsPage };
});
const VerifyEmailPage = lazy(async () => {
  const module = await import('../../pages/VerifyEmailPage');
  return { default: module.VerifyEmailPage };
});
const ExecutionRunsPage = lazy(async () => {
  const module = await import('@/features/executionRuns/pages/ExecutionRunsPage');
  return { default: module.ExecutionRunsPage };
});
const BuildDetailPage = lazy(async () => {
  const module = await import('@/features/builds/routes');
  return { default: module.BuildDetailPage };
});
const JobRunDetailPage = lazy(async () => {
  const module = await import('@/features/builds/routes');
  return { default: module.JobRunDetailPage };
});
const ExecutionRunDetailPage = lazy(async () => {
  const module = await import('@/features/executionRuns/pages/ExecutionRunDetailPage');
  return { default: module.ExecutionRunDetailPage };
});
const FeatureDetailPage = lazy(async () => {
  const module = await import('@/features/features/routes');
  return { default: module.FeatureDetailPage };
});
const FeaturesPage = lazy(async () => {
  const module = await import('@/features/features/routes');
  return { default: module.FeaturesPage };
});
const OnboardingPage = lazy(async () => {
  const module = await import('@/features/onboarding/pages/OnboardingPage');
  return { default: module.OnboardingPage };
});
const OnboardingDebugIndexPage = lazy(async () => {
  const module = await import('@/features/onboardingDebug/pages/OnboardingDebugIndexPage');
  return { default: module.OnboardingDebugIndexPage };
});
const OnboardingDebugScreenPage = lazy(async () => {
  const module = await import('@/features/onboardingDebug/pages/OnboardingDebugScreenPage');
  return { default: module.OnboardingDebugScreenPage };
});
const OrganisationSettingsPage = lazy(async () => {
  const module = await import('@/features/organisations/pages/OrganisationSettingsPage');
  return { default: module.OrganisationSettingsPage };
});
const PipelineDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/PipelineDetailPage');
  return { default: module.PipelineDetailPage };
});
const PipelinesPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/PipelinesPage');
  return { default: module.PipelinesPage };
});
const StagesPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/StagesPage');
  return { default: module.StagesPage };
});
const StageDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/StageDetailPage');
  return { default: module.StageDetailPage };
});
const StageExecutionDetailPage = lazy(async () => {
  const module = await import('@/features/pipelines/pages/StageExecutionDetailPage');
  return { default: module.StageExecutionDetailPage };
});
const JobDetailPage = lazy(async () => {
  const module = await import('@/features/jobs/routes');
  return { default: module.JobDetailPage };
});
const JobsPage = lazy(async () => {
  const module = await import('@/features/jobs/routes');
  return { default: module.JobsPage };
});
const ReleaseDetailPage = lazy(async () => {
  const module = await import('@/features/releases/routes');
  return { default: module.ReleaseDetailPage };
});
const ReleasesPage = lazy(async () => {
  const module = await import('@/features/releases/routes');
  return { default: module.ReleasesPage };
});
const SignInPage = lazy(async () => {
  const module = await import('../../pages/SignInPage');
  return { default: module.SignInPage };
});
const SignUpPage = lazy(async () => {
  const module = await import('../../pages/SignUpPage');
  return { default: module.SignUpPage };
});
const AuthCallbackPage = lazy(async () => {
  const module = await import('../../pages/AuthCallbackPage');
  return { default: module.AuthCallbackPage };
});
const AcceptInvitationPage = lazy(async () => {
  const module = await import('../../pages/AcceptInvitationPage');
  return { default: module.AcceptInvitationPage };
});
const SuiteDetailPage = lazy(async () => {
  const module = await import('@/features/suites/routes');
  return { default: module.SuiteDetailPage };
});
const SuiteRunDetailPage = lazy(async () => {
  const module = await import('@/features/executions/routes');
  return { default: module.SuiteRunDetailPage };
});
const SuitesPage = lazy(async () => {
  const module = await import('@/features/suites/routes');
  return { default: module.SuitesPage };
});
const TestDetailPage = lazy(async () => {
  const module = await import('@/features/tests/routes');
  return { default: module.TestDetailPage };
});
const TestRunDetailPage = lazy(async () => {
  const module = await import('@/features/executions/routes');
  return { default: module.TestRunDetailPage };
});
const TestsPage = lazy(async () => {
  const module = await import('@/features/tests/routes');
  return { default: module.TestsPage };
});
const ResultsFileDetailPage = lazy(async () => {
  const module = await import('@/features/uploads/routes');
  return { default: module.ResultsFileDetailPage };
});
const UploadDetailPage = lazy(async () => {
  const module = await import('@/features/uploads/routes');
  return { default: module.UploadDetailPage };
});
const authBypassClerkEnabled = import.meta.env.VITE_AUTH_BYPASS_CLERK === 'true';

export type AppRouteContext = {
  session: AuthSession | null;
  authLoading: boolean;
  authError: AuthError | null;
  signIn: (input: { email: string; password: string }) => Promise<void>;
  signUp: (input: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    acceptedPolicies: boolean;
  }) => Promise<void>;
  signOut: () => void;
  refreshSession: () => Promise<boolean>;
  setSessionDirect: (next: AuthSession | null) => void;
  isAdmin: boolean;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  projects: ProjectDto[];
  onAddProject: () => void;
};

export type AppRouteDefinition = {
  key: string;
  path: string;
  title?: string;
  description?: string;
  suspenseFallbackLabel?: string;
  hideSuspenseSpinner?: boolean;
  featureFlag?: string;
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  requiresSuperuser?: boolean;
  devOnly?: boolean;
  element: (context: AppRouteContext) => ReactElement;
  nav?: {
    label: string;
    icon: ReactElement;
    requiresAdmin?: boolean;
    requiresSuperuser?: boolean;
    secondary?: string;
    secondaryPath?: string;
    disabled?: boolean;
    secondaryDisabled?: boolean;
  };
};

const createPolicyPage =
  (dataId: string): AppRouteDefinition['element'] =>
  () => <TermlyPolicyEmbed dataId={dataId} />;

export const appRouteDefinitions: AppRouteDefinition[] = [
  {
    key: 'landing',
    path: '/',
    title: 'Next-level Test Results Analytics, Insights & Reporting',
    description:
      'FlowQA helps teams track test runs, surface failure patterns, and understand quality trends across suites, tests, and pipelines.',
    element: () => <LandingPage />,
  },
  {
    key: 'preview',
    path: '/preview',
    title: 'Preview',
    description: 'Preview FlowQA with sample suite data before signing in.',
    suspenseFallbackLabel: 'Loading preview...',
    hideSuspenseSpinner: true,
    element: () => <PreviewPage />,
  },
  {
    key: 'debug-onboarding',
    path: '/__debug/onboarding',
    title: 'Debug: Onboarding',
    devOnly: true,
    element: () => <OnboardingDebugIndexPage />,
  },
  {
    key: 'debug-onboarding-screen',
    path: '/__debug/onboarding/:screen',
    title: 'Debug: Onboarding',
    devOnly: true,
    element: () => <OnboardingDebugScreenPage />,
  },
  { key: 'docs', path: '/docs', title: 'Docs', element: () => <DocsPage /> },
  {
    key: 'cookie-policy',
    path: '/cookie-policy',
    title: 'Cookie Policy',
    element: createPolicyPage('07370c92-4ecc-41fe-836e-4487b26a9aa8'),
  },
  {
    key: 'cookie-policy-html',
    path: '/cookie-policy.html',
    title: 'Cookie Policy',
    element: () => <Navigate to="/cookie-policy" replace />,
  },
  {
    key: 'privacy-requests',
    path: '/privacy-requests',
    title: 'Privacy Requests',
    element: () => <PrivacyRequestsPage />,
  },
  {
    key: 'privacy-policy',
    path: '/privacy-policy',
    title: 'Privacy Policy',
    element: createPolicyPage('9ec55e0f-2e88-41a9-803b-5e1180513847'),
  },
  {
    key: 'privacy-policy-html',
    path: '/privacy-policy.html',
    title: 'Privacy Policy',
    element: () => <Navigate to="/privacy-policy" replace />,
  },
  {
    key: 'terms-of-service',
    path: '/terms-of-service',
    title: 'Terms of Service',
    element: createPolicyPage('6698af93-e6c9-4ffa-8f24-0ff34b3e022d'),
  },
  {
    key: 'acceptable-use-policy',
    path: '/acceptable-use-policy',
    title: 'Acceptable Use Policy',
    element: createPolicyPage('cea0d21a-5c0b-4a92-bd65-7501d1af6e98'),
  },
  {
    key: 'terms-of-service-html',
    path: '/terms-of-service.html',
    title: 'Terms of Service',
    element: () => <Navigate to="/terms-of-service" replace />,
  },
  {
    key: 'sign-in',
    path: `${AUTH_ROUTE_PATHS.signIn}/*`,
    title: 'Sign In',
    element: ({ session, authError, signOut }) => (
      <SignInPage session={session} authError={authError} signOut={signOut} />
    ),
  },
  {
    key: 'sign-up',
    path: `${AUTH_ROUTE_PATHS.signUp}/*`,
    title: 'Sign Up',
    element: ({ session, authLoading, authError, signOut }) => (
      <SignUpPage
        session={session}
        authLoading={authLoading}
        authError={authError}
        signOut={signOut}
      />
    ),
  },
  {
    key: 'auth-callback',
    path: AUTH_ROUTE_PATHS.authCallback,
    title: 'Auth Callback',
    element: () =>
      authBypassClerkEnabled ? <Navigate to="/sign-in" replace /> : <AuthCallbackPage />,
  },
  {
    key: 'accept-invitation',
    path: AUTH_ROUTE_PATHS.acceptInvitation,
    title: 'Accept Invitation',
    element: ({ session, authLoading, authError }) =>
      authBypassClerkEnabled ? (
        <Navigate to="/sign-in" replace />
      ) : (
        <AcceptInvitationPage session={session} authLoading={authLoading} authError={authError} />
      ),
  },
  {
    key: 'reset-password',
    path: AUTH_ROUTE_PATHS.resetPassword,
    title: 'Reset Password',
    element: ({ session }) => <ResetPasswordPage session={session} />,
  },
  {
    key: 'reset-password-confirm',
    path: AUTH_ROUTE_PATHS.resetPasswordConfirm,
    title: 'Choose New Password',
    element: ({ session }) => <ResetPasswordConfirmPage session={session} />,
  },
  {
    key: 'verify-email',
    path: AUTH_ROUTE_PATHS.verifyEmail,
    title: 'Verify Email',
    element: ({ session }) => <VerifyEmailPage session={session} />,
  },
  {
    key: 'onboarding',
    path: '/onboarding',
    title: 'Get Started',
    requiresAuth: true,
    element: ({ session, organisations }) => (
      <OnboardingPage session={session} organisations={organisations} />
    ),
  },
  {
    key: 'reports',
    path: '/reports',
    title: 'Reports',
    requiresAuth: true,
    element: () => <ReportsPage />,
  },
  {
    key: 'settings',
    path: '/account-settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: ({ session }) => <UserSettingsPage session={session} />,
  },
  {
    key: 'settings-redirect',
    path: '/settings',
    title: 'Account Settings',
    requiresAuth: true,
    element: () => <Navigate to="/account-settings" replace />,
  },
  {
    key: 'plan',
    path: '/plan-settings',
    title: 'Plan Settings',
    requiresAuth: true,
    element: () => <PlanPage />,
  },
  {
    key: 'superuser',
    path: '/superuser',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: ({ session }) => <SuperuserPage session={session} />,
    nav: { label: 'Superuser', icon: <SettingsIcon fontSize="small" />, requiresSuperuser: true },
  },
  {
    key: 'feature-flags-redirect',
    path: '/feature-flags',
    title: 'Superuser',
    requiresAuth: true,
    requiresSuperuser: true,
    element: () => <Navigate to="/superuser" replace />,
  },
  {
    key: 'projects',
    path: '/projects',
    title: 'Projects',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'releases',
    path: '/releases',
    title: 'Releases',
    requiresAuth: true,
    element: () => <ReleasesPage />,
    nav: { label: 'Releases', icon: <ReleasesIcon fontSize="small" /> },
  },
  {
    key: 'features',
    path: '/features',
    title: 'Features',
    requiresAuth: true,
    element: () => <FeaturesPage />,
    nav: { label: 'Features', icon: <FeaturesIcon fontSize="small" /> },
  },
  {
    key: 'organisations',
    path: '/organisations/:slug',
    title: 'Workspace',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'organisation-settings',
    path: '/organisation-settings',
    title: 'Workspace Settings',
    requiresAuth: true,
    element: ({ session }) => <OrganisationSettingsPage session={session} />,
  },
  {
    key: 'project-detail',
    path: '/projects/:slug',
    title: 'Project',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'project-settings',
    path: '/project-settings',
    title: 'Project Settings',
    requiresAuth: true,
    element: ({ session }) => <ProjectSettingsPage session={session} />,
  },
  ...codeCoverageRouteDefinitions,
  {
    key: 'execution-runs',
    path: '/execution-runs',
    title: 'Execution Runs',
    description:
      'View execution runs, pass rates, status trends, and upload outcomes across your project.',
    requiresAuth: true,
    element: () => <ExecutionRunsPage />,
    nav: {
      label: 'Execution Runs',
      icon: <PlayCircleFilledWhiteOutlinedIcon fontSize="small" />,
    },
  },
  {
    key: 'uploads-redirect',
    path: '/uploads',
    title: 'Execution Runs',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs" replace />,
  },
  {
    key: 'upload-detail',
    path: '/uploads/:id',
    title: 'Upload',
    requiresAuth: true,
    element: ({ session }) => <UploadDetailPage session={session} />,
  },
  {
    key: 'execution-run-detail',
    path: '/execution-runs/:id',
    title: 'Execution Run',
    requiresAuth: true,
    element: ({ session }) => <ExecutionRunDetailPage session={session} />,
  },
  {
    key: 'suites',
    path: '/suites',
    title: 'Suites',
    requiresAuth: true,
    element: () => <SuitesPage />,
    nav: {
      label: 'Suites',
      icon: <SuitesIcon fontSize="small" />,
    },
  },
  {
    key: 'tests',
    path: '/tests',
    title: 'Tests',
    requiresAuth: true,
    element: () => <TestsPage />,
    nav: {
      label: 'Tests',
      icon: <TestsIcon fontSize="small" />,
    },
  },
  {
    key: 'pipelines',
    path: '/pipelines',
    title: 'Pipelines',
    requiresAuth: true,
    element: () => <PipelinesPage />,
    nav: {
      label: 'Pipelines',
      icon: <BuildsIcon fontSize="small" />,
    },
  },
  {
    key: 'stages',
    path: '/stages',
    title: 'Stages',
    requiresAuth: true,
    element: () => <StagesPage />,
    nav: {
      label: 'Stages',
      icon: <DriveFileMoveIcon fontSize="small" />,
    },
  },
  {
    key: 'jobs',
    path: '/jobs',
    title: 'Jobs',
    requiresAuth: true,
    element: () => <JobsPage />,
    nav: {
      label: 'Jobs',
      icon: <StagesIcon fontSize="small" />,
    },
  },
  {
    key: 'stage-run-detail',
    path: '/stages/:stageSlug/runs/:stageExecutionId',
    title: 'Stage Run',
    requiresAuth: true,
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail',
    path: '/stages/:stageSlug',
    title: 'Stage',
    requiresAuth: true,
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-run-detail',
    path: '/jobs/:jobSlug/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'job-detail',
    path: '/jobs/:jobSlug',
    title: 'Job',
    requiresAuth: true,
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'stage-run-detail-structured-legacy',
    path: '/pipelines/:pipelineId/stages/:stageName/runs/:stageExecutionId',
    title: 'Stage Run',
    requiresAuth: true,
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail-structured-legacy',
    path: '/pipelines/:pipelineId/stages/:stageName',
    title: 'Stage',
    requiresAuth: true,
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-run-detail-structured-legacy',
    path: '/pipelines/:pipelineId/jobs/:stageName/:jobName/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'job-detail-structured-legacy',
    path: '/pipelines/:pipelineId/jobs/:stageName/:jobName',
    title: 'Job',
    requiresAuth: true,
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'job-run-detail-legacy',
    path: '/jobs/runs/:jobRunId',
    title: 'Job Run',
    requiresAuth: true,
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'stage-execution-detail-legacy',
    path: '/stages/executions/:stageExecutionId',
    title: 'Stage Execution',
    requiresAuth: true,
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'stage-detail-legacy',
    path: '/stages/legacy/:stageId',
    title: 'Stage',
    requiresAuth: true,
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'job-detail-legacy',
    path: '/jobs/legacy/:jobId',
    title: 'Job',
    requiresAuth: true,
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'pipeline-detail',
    path: '/pipelines/:pipelineSlug',
    title: 'Pipeline',
    requiresAuth: true,
    element: ({ session }) => <PipelineDetailPage session={session} />,
  },
  {
    key: 'build-detail',
    path: '/pipelines/:pipelineSlug/runs/:runNumber',
    title: 'Job',
    requiresAuth: true,
    element: ({ session }) => <BuildDetailPage session={session} />,
  },
  {
    key: 'results-files-redirect',
    path: '/results-files',
    title: 'Results Files',
    requiresAuth: true,
    element: () => <Navigate to="/execution-runs#results-files" replace />,
  },
  {
    key: 'results-file-detail',
    path: '/results-files/:slug',
    title: 'Results Files',
    requiresAuth: true,
    element: ({ session }) => <ResultsFileDetailPage session={session} />,
  },
  {
    key: 'suite-run-detail',
    path: '/suites/:suiteSlug/runs/:runNumber',
    title: 'Suite Run',
    requiresAuth: true,
    element: ({ session }) => <SuiteRunDetailPage session={session} />,
  },
  {
    key: 'test-run-detail',
    path: '/tests/:testSlug/runs/:runNumber',
    title: 'Test Run',
    requiresAuth: true,
    element: ({ session }) => <TestRunDetailPage session={session} />,
  },
  {
    key: 'feature-detail',
    path: '/features/:slug',
    title: 'Feature',
    requiresAuth: true,
    element: ({ session }) => <FeatureDetailPage session={session} />,
  },
  {
    key: 'release-detail',
    path: '/releases/:slug',
    title: 'Release',
    requiresAuth: true,
    element: ({ session }) => <ReleaseDetailPage session={session} />,
  },
  {
    key: 'suite-detail',
    path: '/suites/:slug',
    title: 'Suite',
    requiresAuth: true,
    element: ({ session }) => <SuiteDetailPage session={session} />,
  },
  {
    key: 'test-detail',
    path: '/tests/:slug',
    title: 'Test',
    requiresAuth: true,
    element: ({ session }) => <TestDetailPage session={session} />,
  },
  {
    key: 'session-expired',
    path: '/session-expired',
    title: 'Session Expired',
    element: () => <SessionExpired />,
  },
];
